import { useState, useEffect } from 'react';
import { apiService, BPReading, BPTrend } from '../utils/api';

export const useBPData = (userId = 'default') => {
  const [readings, setReadings] = useState<BPReading[]>([]);
  const [trend, setTrend] = useState<BPTrend | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [readingsData, trendData] = await Promise.all([
        apiService.getBPReadings(userId, 20), // Last 20 readings
        apiService.getBPTrend(userId, 30)     // 30-day trend
      ]);
      
      setReadings(readingsData);
      setTrend(trendData);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch BP data');
    } finally {
      setLoading(false);
    }
  };

  const addReading = async (reading: Omit<BPReading, 'id' | 'timestamp'>) => {
    try {
      const newReading = await apiService.createBPReading(reading, userId);
      setReadings(prev => [newReading, ...prev]);
      
      // Refresh trend data after adding new reading
      const updatedTrend = await apiService.getBPTrend(userId, 30);
      setTrend(updatedTrend);
      
      return newReading;
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add BP reading');
      throw err;
    }
  };

  const deleteReading = async (id: number) => {
    try {
      await apiService.deleteBPReading(id, userId);
      setReadings(prev => prev.filter(r => r.id !== id));
      
      // Refresh trend data after deleting reading
      const updatedTrend = await apiService.getBPTrend(userId, 30);
      setTrend(updatedTrend);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete BP reading');
      throw err;
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return {
    readings,
    trend,
    loading,
    error,
    refetch: fetchData,
    addReading,
    deleteReading
  };
};